import { motion } from "framer-motion";
import { ArrowRight, Users } from "lucide-react";
import HeroVisual from "./HeroVisual";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      id="hero"
      className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden"
    >
      {/* Background grid */}
      <div className="absolute inset-0 ring-grid opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
      {/* Ambient glows */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-indigo-600/20 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full bg-indigo-500/15 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs uppercase tracking-[0.2em] text-indigo-300 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
            The Dual-Engine Advantage
          </motion.div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
            <span className="block text-white">Engineering</span>
            <span className="block text-white">the Future:</span>
            <span className="block text-gradient mt-2">
              AI Solutions &amp; Expert Talent.
            </span>
          </h1>

          <p className="mt-7 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed">
            We pair intelligent automation with the world&apos;s most specialized
            human talent — a growth engine purpose-built for aerospace, banking,
            energy and IT enterprises.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              data-testid="hero-cta-ai"
              onClick={() => scrollTo("#services")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-medium transition-all duration-300 shadow-[0_0_28px_rgba(99,102,241,0.5)] hover:shadow-[0_0_44px_rgba(99,102,241,0.75)] hover:-translate-y-0.5"
            >
              Explore AI Solutions
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-talent"
              onClick={() => scrollTo("#contact")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full glass text-white font-medium hover:border-white/25 hover:bg-white/[0.08] transition-all duration-300"
            >
              <Users className="w-4 h-4" />
              Discuss Talent Needs
            </button>
          </div>

          <div className="mt-12 flex items-center gap-6 text-xs text-slate-400">
            <div>
              <div className="font-display text-2xl text-white font-light">100+</div>
              <div className="uppercase tracking-widest">Workflows</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <div className="font-display text-2xl text-white font-light">500+</div>
              <div className="uppercase tracking-widest">Experts placed</div>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div>
              <div className="font-display text-2xl text-white font-light">98%</div>
              <div className="uppercase tracking-widest">Satisfaction</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-[420px] md:h-[520px] lg:h-[600px]"
        >
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
}
